import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Phone, Plugs, CheckCircle, XCircle, Warning, Gear, Users, Hash, Shield
} from "@phosphor-icons/react";

export default function TelephonySettingsPage() {
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState(null);
  const [extensions, setExtensions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [newExt, setNewExt] = useState({ userId: "", extension: "" });
  const [activeTab, setActiveTab] = useState("connection");

  useEffect(() => {
    const load = async () => {
      try {
        const [configRes, statusRes, extRes, usersRes] = await Promise.all([
          api.get("/telephony/config").catch(() => ({ data: {} })),
          api.get("/telephony/status").catch(() => ({ data: null })),
          api.get("/telephony/extensions").catch(() => ({ data: [] })),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        setConfig(configRes.data || {});
        setStatus(statusRes.data);
        setExtensions(extRes.data || []);
        setUsers(usersRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const saveConfig = async () => {
    try {
      await api.put("/telephony/config", config);
      toast.success("Telephony settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/telephony/test-connection");
      setStatus(prev => ({ ...prev, ...data }));
      if (data.connected) toast.success("3CX connection successful!");
      else toast.error(data.error || "Connection failed");
    } catch { toast.error("Connection test failed"); }
    finally { setTesting(false); }
  };

  const assignExtension = async () => {
    if (!newExt.userId || !newExt.extension) { toast.error("Select user and enter extension"); return; }
    try {
      await api.put(`/telephony/extensions/${newExt.userId}`, { extension: newExt.extension });
      toast.success("Extension assigned");
      setNewExt({ userId: "", extension: "" });
      const { data } = await api.get("/telephony/extensions");
      setExtensions(data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const removeExtension = async (userId) => {
    try {
      await api.delete(`/telephony/extensions/${userId}`);
      setExtensions(exts => exts.filter(e => e.user_id !== userId));
      toast.success("Extension removed");
    } catch { toast.error("Failed to remove"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-lg" />)}</div>;

  const TABS = [
    { key: "connection", label: "3CX Connection", icon: Plugs },
    { key: "extensions", label: "Extensions", icon: Hash },
    { key: "settings", label: "Dialler Settings", icon: Gear },
  ];

  return (
    <div data-testid="telephony-settings-page" className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Telephony Settings</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Configure 3CX PBX connection, extensions and dialler settings</p>
      </div>

      {/* Status Banner */}
      {status && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          status.connected ? "bg-green-50 border-green-200" :
          status.api_status === "mock_mode" ? "bg-amber-50 border-amber-200" :
          "bg-red-50 border-red-200"
        }`} data-testid="telephony-status-banner">
          {status.connected ? (
            <CheckCircle size={20} className="text-green-600" weight="fill" />
          ) : status.api_status === "mock_mode" ? (
            <Warning size={20} className="text-amber-600" weight="fill" />
          ) : (
            <XCircle size={20} className="text-red-600" weight="fill" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-[#0F172A]">
              {status.connected ? "3CX Connected" : status.api_status === "mock_mode" ? "Mock Mode — 3CX Not Configured" : "3CX Disconnected"}
            </p>
            <p className="text-xs text-[#6B7280]">
              Provider: {status.provider || "None"} | SIP: {status.sip_provider || "Not set"} | Active: {status.active_calls || 0} calls
            </p>
          </div>
          {status.error && <p className="text-xs text-red-600 max-w-xs">{status.error}</p>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F3F4F6] p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === t.key ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`}
            data-testid={`tab-${t.key}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Connection Tab */}
      {activeTab === "connection" && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-5 space-y-4" data-testid="connection-panel">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={18} className="text-[#C5A059]" />
            <h2 className="text-sm font-semibold text-[#0F172A]">3CX PBX Configuration</h2>
          </div>
          <p className="text-xs text-[#6B7280]">Credentials are stored server-side only and never exposed to the browser.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">3CX Instance URL</label>
              <Input placeholder="https://your-company.3cx.com.au" value={config.threecx_url || ""}
                onChange={e => setConfig({...config, threecx_url: e.target.value})} data-testid="threecx-url-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">API Key</label>
              <Input type="password" placeholder="3CX API Key" value={config.threecx_api_key || ""}
                onChange={e => setConfig({...config, threecx_api_key: e.target.value})} data-testid="threecx-api-key-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">SIP Provider</label>
              <Input placeholder="e.g. Gamma" value={config.sip_provider_name || ""}
                onChange={e => setConfig({...config, sip_provider_name: e.target.value})} data-testid="sip-provider-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">DID Numbers</label>
              <Input placeholder="e.g. +44 121 XXX XXXX" value={config.did_numbers || ""}
                onChange={e => setConfig({...config, did_numbers: e.target.value})} data-testid="did-numbers-input" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={saveConfig} className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="save-config-btn">Save Configuration</Button>
            <Button onClick={testConnection} variant="outline" disabled={testing} data-testid="test-connection-btn">
              {testing ? "Testing..." : "Test Connection"}
            </Button>
          </div>
        </div>
      )}

      {/* Extensions Tab */}
      {activeTab === "extensions" && (
        <div className="space-y-4" data-testid="extensions-panel">
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-5">
            <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
              <Users size={16} className="text-[#C5A059]" /> Extension Mappings
            </h2>
            <div className="flex gap-2 mb-4">
              <Select value={newExt.userId} onValueChange={v => setNewExt({...newExt, userId: v})}>
                <SelectTrigger className="flex-1" data-testid="ext-user-select"><SelectValue placeholder="Select recruiter..." /></SelectTrigger>
                <SelectContent>
                  {users.filter(u => !extensions.find(e => e.user_id === u.id)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input placeholder="Extension (e.g. 301)" value={newExt.extension}
                onChange={e => setNewExt({...newExt, extension: e.target.value})} className="w-32" data-testid="ext-number-input" />
              <Button onClick={assignExtension} className="bg-[#C5A059] hover:bg-[#B8933F]" data-testid="assign-ext-btn">Assign</Button>
            </div>
            {extensions.length > 0 ? (
              <div className="divide-y divide-[#F3F4F6]">
                {extensions.map(ext => (
                  <div key={ext.user_id} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0F172A] flex items-center justify-center">
                        <Phone size={14} className="text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">{ext.user_name || ext.user_id}</p>
                        <p className="text-xs text-[#6B7280]">{ext.user_role || ""} | {ext.team_name || "No team"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono font-bold text-[#0F172A]">Ext. {ext.extension}</span>
                      <button onClick={() => removeExtension(ext.user_id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6B7280] text-center py-4">No extensions assigned. Add recruiters above.</p>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-5 space-y-4" data-testid="dialler-settings-panel">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-1">Dialler Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Max Concurrent Calls</label>
              <Input type="number" value={config.max_concurrent_calls || 20}
                onChange={e => setConfig({...config, max_concurrent_calls: parseInt(e.target.value)})} data-testid="max-calls-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Call Retention (days)</label>
              <Input type="number" value={config.call_retention_days || 365}
                onChange={e => setConfig({...config, call_retention_days: parseInt(e.target.value)})} data-testid="retention-input" />
            </div>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-[#0F172A]">Power Dialler</p>
              <p className="text-xs text-[#6B7280]">Enable power dialling for batch call campaigns</p>
            </div>
            <Switch checked={config.power_dialler_enabled !== false}
              onCheckedChange={v => setConfig({...config, power_dialler_enabled: v})} data-testid="power-toggle" />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-[#0F172A]">Call Recording</p>
              <p className="text-xs text-[#6B7280]">Requires UK legal compliance check before enabling</p>
            </div>
            <Switch checked={config.call_recording_enabled || false}
              onCheckedChange={v => setConfig({...config, call_recording_enabled: v})} data-testid="recording-toggle" />
          </div>
          {config.call_recording_enabled && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800"><Warning size={14} className="inline mr-1" />
                Call recording requires: UK data protection compliance, caller notification, Banks Morgan policy approval, and adequate 3CX storage configuration.
              </p>
            </div>
          )}
          <Button onClick={saveConfig} className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="save-dialler-settings-btn">Save Settings</Button>
        </div>
      )}
    </div>
  );
}
